import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Calendar, Clock, Users, HardHat, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useRetraitChantierBulk, RetraitData } from '@/hooks/useRetraitChantierBulk';
import { cn } from '@/lib/utils';

// ============================================================
// TYPES
// ============================================================

interface Membre {
  id: string;
  prenom: string;
  nom: string;
}

interface Affectation {
  id: string;
  chantier_id: string;
  chantier_nom: string;
  date_debut: string;
  date_fin: string;
  heures_par_jour: number | null;
}

interface ModalRetraitChantierBulkProps {
  isOpen: boolean;
  onClose: () => void;
  membres: Membre[];
  // Record des affectations actives par membre (membreId → affectation)
  affectationsActives: Record<string, Affectation>;
  nouveauStatut: 'disponible' | 'repos' | 'en_arret';
  onSuccess: () => void;
}

// État par membre pour le formulaire
interface MembreRetraitState {
  membreId: string;
  affectationId: string;
  dateDebut: string;
  dernierJour: string;
  heuresDernierJour: number;
  chantierNom: string;
  dateFinPrevue: string;
}

const STATUT_LABELS: Record<string, { label: string; emoji: string }> = {
  disponible: { label: 'Disponible', emoji: '✅' },
  repos: { label: 'Repos', emoji: '🏠' },
  en_arret: { label: 'En arrêt', emoji: '🏥' },
};

// ============================================================
// COMPOSANT
// ============================================================

export function ModalRetraitChantierBulk({
  isOpen,
  onClose,
  membres,
  affectationsActives,
  nouveauStatut,
  onSuccess,
}: ModalRetraitChantierBulkProps) {
  const { retirerMembres, isLoading } = useRetraitChantierBulk();

  // État pour chaque membre
  const [membresState, setMembresState] = useState<MembreRetraitState[]>([]);

  // Date commune pour "Appliquer à tous"
  const [dateCommune, setDateCommune] = useState('');

  // Initialiser les états à l'ouverture
  useEffect(() => {
    if (isOpen && membres.length > 0) {
      const today = new Date().toISOString().split('T')[0];
      setDateCommune(today);

      const initialState: MembreRetraitState[] = membres.map((membre) => {
        const affectation = affectationsActives[membre.id];
        return {
          membreId: membre.id,
          affectationId: affectation?.id || '',
          dateDebut: affectation?.date_debut?.split('T')[0] || today,
          dernierJour: today,
          heuresDernierJour: affectation?.heures_par_jour || 8,
          chantierNom: affectation?.chantier_nom || 'Chantier inconnu',
          dateFinPrevue: affectation?.date_fin?.split('T')[0] || today,
        };
      });

      setMembresState(initialState);
    }
  }, [isOpen, membres, affectationsActives]);

  // Mettre à jour un champ pour un membre
  const updateMembreField = (membreId: string, field: keyof MembreRetraitState, value: string | number) => {
    setMembresState((prev) =>
      prev.map((m) => (m.membreId === membreId ? { ...m, [field]: value } : m))
    );
  };

  // Appliquer la date commune à tous
  const appliquerDateCommune = () => {
    if (!dateCommune) return;
    setMembresState((prev) =>
      prev.map((m) => ({ ...m, dernierJour: dateCommune }))
    );
  };

  // Valider le formulaire
  const isFormValid = useMemo(() => {
    return membresState.every((m) => {
      // Date valide et pas dans le futur
      const today = new Date().toISOString().split('T')[0];
      return (
        m.affectationId &&
        m.dernierJour &&
        m.dernierJour <= today &&
        m.dernierJour >= m.dateDebut &&
        m.heuresDernierJour > 0 &&
        m.heuresDernierJour <= 16
      );
    });
  }, [membresState]);

  // Nombre de membres avec dates différentes de la date commune
  const membresAvecDateDifferente = useMemo(() => {
    return membresState.filter((m) => m.dernierJour !== dateCommune).length;
  }, [membresState, dateCommune]);

  // Confirmer le retrait
  const handleConfirm = async () => {
    if (!isFormValid) return;

    const retraits: RetraitData[] = membresState.map((m) => ({
      membreId: m.membreId,
      affectationId: m.affectationId,
      dateDebut: m.dateDebut,
      dernierJour: m.dernierJour,
      heuresDernierJour: m.heuresDernierJour,
    }));

    const result = await retirerMembres(retraits, nouveauStatut);

    if (result.success) {
      onSuccess();
      onClose();
    }
  };

  // Libellé du statut
  const statutInfo = STATUT_LABELS[nouveauStatut] || { label: nouveauStatut, emoji: '📋' };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-orange-500" />
            Retrait de {membres.length} membre{membres.length > 1 ? 's' : ''} des chantiers
          </DialogTitle>
          <DialogDescription>
            Indiquez le dernier jour travaillé pour chaque membre. Les affectations seront ajustées et les coûts recalculés.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Nouveau statut */}
          <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50">
            <span className="text-sm text-muted-foreground">Nouveau statut :</span>
            <Badge variant="secondary" className="text-sm">
              {statutInfo.emoji} {statutInfo.label}
            </Badge>
          </div>

          {/* Date commune */}
          <div className="p-3 rounded-lg border border-dashed">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Label className="text-sm text-muted-foreground">Date commune (optionnel)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="date"
                    value={dateCommune}
                    onChange={(e) => setDateCommune(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-40"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={appliquerDateCommune}
                    disabled={!dateCommune}
                  >
                    Appliquer à tous
                  </Button>
                </div>
              </div>
              {membresAvecDateDifferente > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {membresAvecDateDifferente} date{membresAvecDateDifferente > 1 ? 's' : ''} personnalisée{membresAvecDateDifferente > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>

          {/* Liste des membres */}
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {membresState.map((membreState) => {
              const membre = membres.find((m) => m.id === membreState.membreId);
              if (!membre) return null;

              const isDateModifiee = membreState.dernierJour !== dateCommune;
              const isDateInvalide =
                membreState.dernierJour > new Date().toISOString().split('T')[0] ||
                membreState.dernierJour < membreState.dateDebut;

              return (
                <div
                  key={membreState.membreId}
                  className={cn(
                    'p-3 rounded-lg border',
                    isDateInvalide && 'border-red-500 bg-red-500/5',
                    isDateModifiee && !isDateInvalide && 'border-orange-500/50 bg-orange-500/5'
                  )}
                >
                  {/* Header membre */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {membre.prenom} {membre.nom}
                      </span>
                      {isDateModifiee && (
                        <Badge variant="outline" className="text-[10px] text-orange-600">
                          Date personnalisée
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <HardHat className="h-3.5 w-3.5" />
                      <span className="truncate max-w-[150px]">{membreState.chantierNom}</span>
                    </div>
                  </div>

                  {/* Formulaire */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Dernier jour travaillé
                      </Label>
                      <Input
                        type="date"
                        value={membreState.dernierJour}
                        onChange={(e) => updateMembreField(membreState.membreId, 'dernierJour', e.target.value)}
                        min={membreState.dateDebut}
                        max={new Date().toISOString().split('T')[0]}
                        className={cn('mt-1', isDateInvalide && 'border-red-500')}
                      />
                      {isDateInvalide && (
                        <span className="text-[10px] text-red-500">
                          Date invalide (doit être entre {format(parseISO(membreState.dateDebut), 'dd/MM', { locale: fr })} et aujourd'hui)
                        </span>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Heures ce jour
                      </Label>
                      <div className="flex items-center gap-1 mt-1">
                        <Input
                          type="number"
                          value={membreState.heuresDernierJour}
                          onChange={(e) =>
                            updateMembreField(
                              membreState.membreId,
                              'heuresDernierJour',
                              Math.min(16, Math.max(0.5, parseFloat(e.target.value) || 8))
                            )
                          }
                          min={0.5}
                          max={16}
                          step={0.5}
                          className="w-20"
                        />
                        <span className="text-sm text-muted-foreground">h</span>
                      </div>
                    </div>
                  </div>

                  {/* Info date fin prévue */}
                  <div className="mt-2 text-xs text-muted-foreground">
                    Fin prévue initialement :{' '}
                    <span className="font-medium">
                      {format(parseISO(membreState.dateFinPrevue), 'dd/MM/yyyy', { locale: fr })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 p-3 rounded-lg border border-orange-500/50 bg-orange-500/10">
            <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-orange-700">
              <strong>Important :</strong> Les affectations seront tronquées à ces dates.
              Les jours travaillés et les coûts associés seront recalculés automatiquement.
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!isFormValid || isLoading}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Retrait en cours...
              </>
            ) : (
              `Confirmer le retrait (${membres.length})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
